import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'

import App from './App.vue'

import './style.css'

import { createPinia } from 'pinia'
import { setupLckDesignSystem } from '@locokit/designsystem'
import ToastService from 'primevue/toastservice'

import { localI18n } from './plugins/i18n'

import router from './router'
import { useStoreAuth } from './stores/auth'
import { useStoreApp } from './stores/app'
import { useStoreData } from './stores/data'

import { isValid } from 'date-fns'
import { configure, defineRule } from 'vee-validate'
import { email, required, regex } from '@vee-validate/rules'

import { updatePreset } from '@primevue/themes'
import { dexieStorageInstance } from './services/storage/dexie'

/**
 * Boot the VueJS application
 *
 * * reload the authentication data
 * * if we are offline, set the app to be OK working offline
 * * if we are online, try to trigger the sync if needed
 */
async function boot() {
  const app = createApp(App)

  Sentry.init({
    app,
    dsn: 'https://48dec9bcda8faedc1bb756341ec873b3@sentry.makina-corpus.net/62',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })

  app.use(createPinia())

  /**
   * Initialize storage adapter (Dexie/IndexedDB with localStorage fallback)
   * Must be done before any storage operations
   */
  await dexieStorageInstance.isAvailable()

  /**
   * Register events online / offline
   */
  const { updateOnlineStatus, appState } = useStoreApp()
  window.addEventListener('online', updateOnlineStatus)
  window.addEventListener('offline', updateOnlineStatus)

  /**
   * Re authent user before router guards
   */
  const { reAuthenticate } = useStoreAuth()
  await reAuthenticate()

  addEventListener('languagechange', (event) => {
    console.log('languagechange', event, navigator.language)
  })

  setupLckDesignSystem(app, localI18n)
  app.use(router)
  app.use(ToastService)

  /**
   * Patch for vee validate rules
   *
   * need to be integrated in design system, I think
   */
  defineRule('email', email)
  defineRule('required', required)
  defineRule('regex', regex)
  // Todo: Find a fix or fix it
  defineRule('confirmed', (value, [target], ctx) => {
    // To interpret string keypath: "toto.ro" -> toto.ro
    const result = target.split('.').reduce((acc: never, current: string) => {
      return acc[current]
    }, ctx.form)
    return value === result
  })

  defineRule('dateValid', (value: Date) => {
    return isValid(value)
  })

  /**
   * Test if a value is an object
   * but accept an empty string / undefined value too.
   * (if the field has the required rule, it won't be valid)
   */
  defineRule('reference', (value: any | undefined) => {
    return typeof value === 'object' || value === undefined || value === ''
  })

  defineRule('snakeCase', (value: string) => {
    return /^[a-z_]+$/.test(value)
  })

  configure({
    bails: false,
    validateOnBlur: true,
    validateOnChange: false,
    validateOnInput: false,
    validateOnModelUpdate: false,
    generateMessage: (context) => {
      // console.log(context)
      const field = localI18n.global.t(`capel.fields.${context.field}`, context.field)
      // console.log(field)
      return localI18n.global.t(`capel.validations.messages.${context.rule?.name!}`, {
        field
      }) as string
    }
  })

  /**
   * Code needed for focus directive in PrimeVue
   */
  app.directive('focus', {
    mounted(el: HTMLElement) {
      el.focus()
    }
  })

  updatePreset({
    semantic: {
      primary: {
        50: '#f0f9fb',
        100: '#daeff3',
        200: '#bae0e7',
        300: '#8ac8d6',
        400: '#52a8be',
        500: '#3991aa',
        600: '#30718a',
        700: '#2d5d71',
        800: '#2c4e5e',
        900: '#284351',
        950: '#162b36'
      }
    }
  })

  /**
   * Trigger a sync if online
   */
  if (appState.isOnline) {
    const { syncData } = useStoreData()
    syncData()
  }

  app.mount('#app')
}

boot()
